import {
  Component,
  useEffect,
  useRef,
  useState,
  type ErrorInfo,
  type ReactNode,
  type VideoHTMLAttributes,
} from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import type { Scene } from './media';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

if (import.meta.env.DEV && typeof window !== 'undefined') {
  // verification hooks (?raf workflow) — never shipped in production builds
  const w = window as unknown as Record<string, unknown>;
  w.__gsap = gsap;
  w.__ST = ScrollTrigger;
}

export { gsap, ScrollTrigger };

export const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Real phones / tablets — coarse pointer. Chrome DevTools “mobile view”
 *  usually still reports a fine pointer + wheel, which is why shake only
 *  shows up on an actual device. */
export const preferNativeScroll = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(pointer: coarse)').matches ||
    window.matchMedia('(hover: none) and (pointer: coarse)').matches);

/** Lenis needs transform pins; native mobile scroll shakes with them —
 *  use fixed pins on touch devices. */
export const cinematicPinType = (): 'fixed' | 'transform' =>
  preferNativeScroll() ? 'fixed' : 'transform';

/** Scrub lag (seconds) feels like rubber-band shake on touch. On mobile,
 *  link the animation directly to the scroll position. */
export const cinematicScrub = (desktopLag = 0.55): boolean | number =>
  preferNativeScroll() ? true : desktopLag;


/* ------------------------------------------------------------------ */
/* ScrollTrigger refresh — lazy sections shift layout as they stream   */
/* in; a debounced global refresh keeps every trigger position true.   */
/* ------------------------------------------------------------------ */

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
/** While the hero entrance runs, queue refreshes instead of re-pinning
 *  (that flashed the headline/WebGL). Flush once when the gate opens. */
let heroRefreshLocked = true;
let pendingRefreshDelay = 0;

/** Debounced ScrollTrigger.refresh — coalesces layout thrash from lazy
 *  sections / fonts. Too-eager refresh re-pins the hero and reads as flicker. */
export function scheduleRefresh(delay = 420) {
  if (heroRefreshLocked) {
    pendingRefreshDelay = Math.max(pendingRefreshDelay, delay);
    return;
  }
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => ScrollTrigger.refresh(), delay);
}

/** Open the post-hero refresh gate and flush any queued layout sync.
 *  Pins must refresh before downstream section triggers (see refreshPriority).
 *  Safe to call repeatedly — later calls only re-debounce a refresh. */
export function unlockHeroRefresh(delay = 180) {
  const wasLocked = heroRefreshLocked;
  heroRefreshLocked = false;
  const d = wasLocked ? Math.max(delay, pendingRefreshDelay || 0) : delay;
  pendingRefreshDelay = 0;
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    ScrollTrigger.refresh();
  }, d);
}

/** Call from every lazy-mounted section so pinned scenes stay in sync. */
export function useSectionMounted() {
  useEffect(() => {
    scheduleRefresh(520);
    return () => scheduleRefresh(520);
  }, []);
}

/* ------------------------------------------------------------------ */
/* Reveal helper — always fromTo (StrictMode-safe: `gsap.from` captures */
/* mutated values on React double-mount and animates INTO the hidden    */
/* state, which is what froze the pricing cards).                       */
/* ------------------------------------------------------------------ */

export function reveal(
  targets: gsap.TweenTarget,
  from: gsap.TweenVars,
  to: gsap.TweenVars,
  trigger: ScrollTrigger.Vars
) {
  return gsap.fromTo(
    targets,
    { ...from },
    {
      ...to,
      overwrite: 'auto',
      scrollTrigger: { toggleActions: 'play none none none', ...trigger },
    }
  );
}

/** The page's active Lenis instance, if any (landing page only — see
 *  useSmoothScroll). Exposed so cross-page concerns like scroll restoration
 *  can move the same virtual scroll position Lenis is driving, instead of
 *  fighting it with a raw `window.scrollTo`. */
let activeLenis: Lenis | null = null;
export function getLenis(): Lenis | null {
  return activeLenis;
}

/** Lenis smooth scrolling driven by the GSAP ticker, synced to ScrollTrigger.
 *  Disabled on real touch devices — Lenis + transform pins fight native
 *  momentum scrolling and make the globe / pinned chapters shake. */
export function useSmoothScroll() {
  useEffect(() => {
    if (reducedMotion() || preferNativeScroll()) return;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true, syncTouch: false });
    activeLenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);
    const raf = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    // Width-only refresh: body height churns as lazy chapters stream in;
    // refreshing on every height tick re-pins the hero and flickers globe/text.
    let lastW = window.innerWidth;
    const ro = new ResizeObserver(() => {
      const w = window.innerWidth;
      if (Math.abs(w - lastW) < 2) return;
      lastW = w;
      scheduleRefresh(480);
    });
    ro.observe(document.body);
    return () => {
      ro.disconnect();
      gsap.ticker.remove(raf);
      lenis.destroy();
      if (activeLenis === lenis) activeLenis = null;
    };
  }, []);
}

/* ------------------------------------------------------------------ */
/* Idle-mount gate                                                     */
/* ------------------------------------------------------------------ */

/** Delays mounting its subtree until the page has painted and the main
 *  thread is idle. React's Suspense fires every lazy `import()` inside a
 *  boundary as soon as it's rendered — not when it scrolls into view — so
 *  the only way to keep below-the-fold chunks from competing with the
 *  hero's own critical resources for bandwidth/CPU during the LCP window
 *  is to delay the render itself. */
export function useIdleMounted(timeout = 1200) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let idleId = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const start = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => setReady(true), { timeout });
      } else {
        timer = setTimeout(() => setReady(true), 250);
      }
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
    return () => {
      window.removeEventListener('load', start);
      if (idleId && 'cancelIdleCallback' in window) window.cancelIdleCallback(idleId);
      clearTimeout(timer);
    };
  }, [timeout]);
  return ready;
}

/* ------------------------------------------------------------------ */
/* Scroll velocity (normalized-ish px/ms → used by particles/stars)    */
/* ------------------------------------------------------------------ */

export const scrollVelocity = { v: 0 };
if (typeof window !== 'undefined') {
  let lastY = window.scrollY;
  let lastT = performance.now();
  window.addEventListener(
    'scroll',
    () => {
      const now = performance.now();
      const dt = Math.max(now - lastT, 1);
      scrollVelocity.v = (window.scrollY - lastY) / dt;
      lastY = window.scrollY;
      lastT = now;
    },
    { passive: true }
  );
}

/* ------------------------------------------------------------------ */
/* Micro-interactions                                                  */
/* ------------------------------------------------------------------ */

/** Magnetic pull toward the cursor (desktop pointers only). */
export function useMagnetic<T extends HTMLElement>(strength = 0.3) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const xTo = gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' });
    const move = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      xTo((e.clientX - (r.left + r.width / 2)) * strength);
      yTo((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const leave = () => {
      xTo(0);
      yTo(0);
    };
    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    return () => {
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
    };
  }, [strength]);
  return ref;
}

/** 3D tilt on hover for cards. Attach the returned props to the card. */
export function useTilt<T extends HTMLElement>(max = 7) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || reducedMotion()) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const rxTo = gsap.quickTo(el, 'rotationX', { duration: 0.6, ease: 'power3.out' });
    const ryTo = gsap.quickTo(el, 'rotationY', { duration: 0.6, ease: 'power3.out' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.55, ease: 'power3.out' });
    const sTo = gsap.quickTo(el, 'scale', { duration: 0.55, ease: 'power3.out' });
    gsap.set(el, { transformPerspective: 900 });
    const move = (e: MouseEvent) => {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      rxTo(-py * max);
      ryTo(px * max);
    };
    const enter = () => {
      yTo(-10);
      sTo(1.015);
    };
    const leave = () => {
      rxTo(0);
      ryTo(0);
      yTo(0);
      sTo(1);
    };
    el.addEventListener('mouseenter', enter);
    el.addEventListener('mousemove', move);
    el.addEventListener('mouseleave', leave);
    return () => {
      el.removeEventListener('mouseenter', enter);
      el.removeEventListener('mousemove', move);
      el.removeEventListener('mouseleave', leave);
    };
  }, [max]);
  return ref;
}

/* ------------------------------------------------------------------ */
/* Media                                                               */
/* ------------------------------------------------------------------ */

/** Fullscreen/background video: local file first, CDN fallback.
 *  `preload="none"` + IntersectionObserver keeps Lighthouse happy —
 *  nothing below the fold downloads until it approaches the viewport. */
export function SceneVideo({
  scene,
  className,
  ...rest
}: { scene: Scene; className?: string } & VideoHTMLAttributes<HTMLVideoElement>) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          if (el.preload === 'none') el.preload = 'auto';
          el.play().catch(() => {});
        } else el.pause();
      },
      { rootMargin: '45% 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <video
      ref={ref}
      className={className}
      muted
      loop
      playsInline
      preload="none"
      poster={scene.poster}
      {...rest}
    >
      <source src={scene.local} type="video/mp4" />
      {scene.remote && <source src={scene.remote} type="video/mp4" />}
    </video>
  );
}

/** Animated aurora mesh painted on a 2D canvas — cheap, GPU-composited. */
export function AuroraCanvas({
  className,
  intensity = 1,
}: {
  className?: string;
  intensity?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let raf = 0;
    let t = Math.random() * 40;
    let running = true;
    const rm = reducedMotion();

    const blobs = [
      { c: [55, 227, 200], r: 0.52, sx: 0.24, sy: 0.35, vx: 0.31, vy: 0.23, a: 0.16 },
      { c: [124, 108, 255], r: 0.6, sx: 0.72, sy: 0.3, vx: 0.23, vy: 0.31, a: 0.15 },
      { c: [255, 179, 92], r: 0.45, sx: 0.5, sy: 0.75, vx: 0.17, vy: 0.27, a: 0.1 },
      { c: [255, 108, 159], r: 0.38, sx: 0.85, sy: 0.7, vx: 0.27, vy: 0.19, a: 0.08 },
    ];

    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';
      for (const b of blobs) {
        const x = (b.sx + Math.sin(t * b.vx) * 0.14) * w;
        const y = (b.sy + Math.cos(t * b.vy) * 0.12) * h;
        const r = b.r * Math.max(w, h) * (1 + Math.sin(t * 0.4 + b.sx * 9) * 0.08);
        const g = ctx.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, `rgba(${b.c[0]},${b.c[1]},${b.c[2]},${b.a * intensity})`);
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }
      t += 0.008;
      if (!rm && running) raf = requestAnimationFrame(draw);
    };

    // only animate while on screen
    const io = new IntersectionObserver(([e]) => {
      running = e.isIntersecting;
      cancelAnimationFrame(raf);
      if (running) draw();
    });
    io.observe(canvas);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [intensity]);
  return <canvas ref={canvasRef} className={className} aria-hidden />;
}

/** Floating golden particles (pure CSS animation, GPU-composited). */
export function Particles({ count = 26 }: { count?: number }) {
  const [seeds] = useState(() =>
    Array.from({ length: count }, () => ({
      left: Math.random() * 100,
      top: 30 + Math.random() * 70,
      dur: 7 + Math.random() * 9,
      delay: Math.random() * 10,
      size: 2 + Math.random() * 2.5,
    }))
  );
  return (
    <div className="cine-particles" aria-hidden>
      {seeds.map((s, i) => (
        <i
          key={i}
          style={{
            left: `${s.left}%`,
            top: `${s.top}%`,
            width: s.size,
            height: s.size,
            animationDuration: `${s.dur}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

export function GlobeMark({ size = 26 }: { size?: number }) {
  return (
    <img
      src="/branding/rovera-mark.png"
      alt=""
      aria-hidden
      className="cine-brand-mark"
      width={Math.round(size * 1.5)}
      height={size}
      style={{ height: size, width: 'auto' }}
    />
  );
}

/** Section shell with kicker/heading reveal on scroll. */
export function SectionHead({
  kicker,
  title,
  lead,
  center,
}: {
  kicker: string;
  title: ReactNode;
  lead?: string;
  center?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (reducedMotion()) return;
    const ctx = gsap.context(() => {
      reveal(
        ref.current!.children,
        { y: 46, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 1.1, stagger: 0.12, ease: 'power3.out' },
        { trigger: ref.current, start: 'top 82%' }
      );
    }, ref);
    return () => ctx.revert();
  }, []);
  return (
    <div ref={ref} className={`cine-sec-head${center ? ' center' : ''}`}>
      <span className="cine-kicker">{kicker}</span>
      <h2 className="cine-h2">{title}</h2>
      {lead && <p className="cine-lead">{lead}</p>}
    </div>
  );
}

/** Catches render/WebGL-context failures from a three.js scene (some
 *  browsers/devices refuse to create a WebGL context — old GPUs, disabled
 *  hardware acceleration, locked-down corporate policies) so the page keeps
 *  its layout, copy, and CTAs instead of the whole app going blank. */
export class WebGLBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.DEV) console.warn('[WebGLBoundary] scene failed, falling back:', error, info.componentStack);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}
